import { AlertType, TowerAlert } from '../../../common/Alerts';
import { useEffect, useState } from 'react';
import { useFetchAuthenticationRequirementsMutation } from '../apis/loginApi';
import { AuthenticationRequirements } from '../types';
import useSsoRedirect from './useSsoRedirect';
import isEmpty from 'lodash/isEmpty';
import { useSendContactCodeEmailMutation } from '../apis/contactVerifyApi';

const DEFAULT_REQUIREMENTS: AuthenticationRequirements = {
    isSsoRequired: false,
    isMfaRequired: false,
};

export type Response = {
    onAuthenticationRequirementsCheck: (username: string) => void;
    resendContactCodeEmail: () => void;
    isLoading: boolean;
    alerts: TowerAlert[];
    resetAlerts: () => void;
    isPasswordLoginAllowed: boolean;
    hasFetchedRequirements: boolean;
    reset: () => void;
};
export function useAuthenticationRequirementsRouting(): Response {
    const [alerts, setAlerts] = useState<TowerAlert[]>([]);
    const [hasFetchedRequirements, setHasFetchedRequirements] = useState<boolean>(false);
    const [username, setUsername] = useState<string>('');
    const [sendContactCodeEmail] = useSendContactCodeEmailMutation();

    const [
        fetchAuthenticationRequirements,
        {
            data,
            isLoading: isCheckLoading,
            isError: isCheckError,
            reset: resetAuthenticationRequirementsCheck,
        },
    ] = useFetchAuthenticationRequirementsMutation({
        fixedCacheKey: 'authentication-requirements',
    });
    const requirements = data || DEFAULT_REQUIREMENTS;

    // Handles the SSO redirect if SSO is required.
    const { isLoading: isRedirectLoading, isError: isRedirectError } = useSsoRedirect(
        username,
        requirements.isSsoRequired
    );

    const isLoading = isCheckLoading || isRedirectLoading;

    const isError = isCheckError || isRedirectError;
    useEffect(() => {
        if (isError) {
            const message = isCheckError
                ? 'Error fetching authentication requirements'
                : 'Error during Single Sign On redirect.';
            setAlerts([{ type: AlertType.error, message }]);
        } else if (alerts.length > 0) {
            setAlerts([]);
        }
    }, [isError]);

    useEffect(() => {
        if (!isLoading && !isEmpty(username) && alerts.length === 0) {
            // This will pick up after the check occurs.
            setHasFetchedRequirements(true);
        }
    }, [isLoading, username, requirements.isSsoRequired]);

    const runAuthenticationRequirementsCheck = async (username: string) => {
        setUsername(username);
        setHasFetchedRequirements(false);
        fetchAuthenticationRequirements(username);
    };

    const onReset = () => {
        setAlerts([]);
        setHasFetchedRequirements(false);
        setUsername('');
        resetAuthenticationRequirementsCheck();
    };

    const resendContactCodeEmail = () => {
        sendContactCodeEmail(username);
        setAlerts([{ type: AlertType.success, message: 'Code resent successfully.' }]);

        // Clear alerts after 5 seconds
        setTimeout(() => {
            setAlerts([]);
        }, 5000);
    };

    return {
        onAuthenticationRequirementsCheck: runAuthenticationRequirementsCheck,
        resendContactCodeEmail,
        isLoading,
        alerts,
        resetAlerts: () => setAlerts([]),
        isPasswordLoginAllowed: hasFetchedRequirements ? !requirements.isSsoRequired : false,
        hasFetchedRequirements,
        reset: onReset,
    };
}
